import React, { forwardRef, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import MaskedInput from "react-text-mask";

const ClosePeriodMask = forwardRef(function ClosePeriodMask(props, ref) {
  return (
    <MaskedInput
      {...props}
      ref={ref.inputElement}
      mask={[/\d/, /\d/, "/", /\d/, /\d/]}
      guide={false}
      keepCharPositions={false}
      placeholderChar={"\u2000"}
      showMask
    />
  );
});

const trialContractTermOptions = [
  { value: "1", label: "30/60" },
  { value: "2", label: "45/45" },
  { value: "3", label: "60/30" },
  { value: "4", label: "90 (direto)" },
  { value: "5", label: "Outro" },
];

const hasSpecificRegistrationRulesOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const useSamePayrollRegistrationOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const workTimeControlOptions = [
  { value: "1", label: "Manual" },
  { value: "2", label: "Eletrônico" },
];

const timeTrackingClosePeriodOptions = [
  { value: "1", label: "16/15" },
  { value: "2", label: "21/20" },
  { value: "3", label: "26/25" },
  { value: "4", label: "Outro" },
];

const hasBankOfHoursOptions = [
  { value: "1", label: "Sim" },
  { value: "2", label: "Não" },
];

const bankOfHoursFrequencyOptions = [
  { value: "1", label: "1 vez ao ano" },
  { value: "2", label: "2 vezes ao ano" },
  { value: "3", label: "3 vezes ao ano" },
  { value: "4", label: "4 vezes ao ano" },
];

const monthsOptions = [
  { value: "1", label: "Janeiro" },
  { value: "2", label: "Fevereiro" },
  { value: "3", label: "Março" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Maio" },
  { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

const sectionTitleSx = {
  color: "#7A7A7A",
  fontWeight: "600",
  fontSize: "14px",
  my: 0.5,
};

export default function Admission() {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const admissionIsValidated = watch("admissionIsValidated");
  const trialContractTerm = watch("trialContractTerm");
  const trialContractTermFirst = watch("trialContractTermFirst");
  const hasSpecificRegistrationRules = watch("hasSpecificRegistrationRules");
  const timeTrackingClosePeriod = watch("timeTrackingClosePeriod");
  const timeTrackingClosePeriodOther = watch("timeTrackingClosePeriodOther");
  const hasBankOfHours = watch("hasBankOfHours");
  const bankOfHoursFrequency = watch("bankOfHoursFrequency");

  const firstVal = parseInt(trialContractTermFirst, 10) || 0;

  useEffect(() => {
    if (firstVal >= 90) {
      setValue("trialContractTermSecond", "");
    }
  }, [firstVal, setValue]);

  useEffect(() => {
    if (!timeTrackingClosePeriodOther) return;
    const digits = timeTrackingClosePeriodOther.replace(/\D/g, "");
    if (digits.length === 2) {
      const day1 = parseInt(digits, 10);
      if (day1 >= 1) {
        const day2 = day1 === 1 ? 31 : day1 - 1;
        const filled = `${String(day1).padStart(2, "0")}/${String(day2).padStart(2, "0")}`;
        if (timeTrackingClosePeriodOther !== filled) {
          setValue("timeTrackingClosePeriodOther", filled);
        }
      }
    }
  }, [timeTrackingClosePeriodOther, setValue]);

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        mx: { xs: 0, sm: 1, md: 2 },
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" sx={sectionTitleSx}>
          Admissão
        </Typography>

        <Controller
          name="admissionIsValidated"
          control={control}
          render={({ field }) => (
            <FormControl error={!!errors.admissionIsValidated}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox {...field} checked={!!field.value} size="small" />
                <Typography variant="body2">Validado</Typography>
              </Box>
              <FormHelperText>
                {errors.admissionIsValidated?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
      </Box>

      <Grid container spacing={1.5}>
        {/* trialContractTerm — dependentes na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Controller
                name="trialContractTerm"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.trialContractTerm}>
                    <InputLabel id="trial-contract-term-label">
                      Prazo estabelecido do contrato de experiência
                    </InputLabel>
                    <Select
                      {...field}
                      required
                      disabled={admissionIsValidated}
                      labelId="trial-contract-term-label"
                      label="Prazo estabelecido do contrato de experiência"
                    >
                      {trialContractTermOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.trialContractTerm?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {trialContractTerm === "5" && (
              <>
                <Grid item xs={6} md={3}>
                  <Controller
                    name="trialContractTermFirst"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        disabled={admissionIsValidated}
                        label="1ª Experiência"
                        variant="outlined"
                        size="small"
                        fullWidth
                        inputProps={{ inputMode: "numeric" }}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          const num = parseInt(val, 10);
                          if (!val || num <= 90) {
                            field.onChange(val);
                          } else {
                            field.onChange("90");
                          }
                        }}
                        error={!!errors.trialContractTermFirst}
                        helperText={errors.trialContractTermFirst?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6} md={3}>
                  <Controller
                    name="trialContractTermSecond"
                    control={control}
                    render={({ field }) => {
                      const maxSecond = 90 - firstVal;
                      const isDisabled = admissionIsValidated || firstVal <= 0 || firstVal >= 90;
                      return (
                        <TextField
                          {...field}
                          required
                          disabled={isDisabled}
                          label="2ª Experiência"
                          variant="outlined"
                          size="small"
                          fullWidth
                          inputProps={{ inputMode: "numeric" }}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            const num = parseInt(val, 10);
                            if (!val || num <= maxSecond) {
                              field.onChange(val);
                            } else {
                              field.onChange(String(maxSecond));
                            }
                          }}
                          error={!!errors.trialContractTermSecond}
                          helperText={errors.trialContractTermSecond?.message}
                        />
                      );
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>

        {/* hasSpecificRegistrationRules — dependente na mesma linha */}
        <Grid item xs={12}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Controller
                name="hasSpecificRegistrationRules"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.hasSpecificRegistrationRules}>
                    <InputLabel id="has-specific-registration-rules-label">
                      Existe regra específica para o cadastro de matrículas por categoria
                    </InputLabel>
                    <Select
                      {...field}
                      required
                      disabled={admissionIsValidated}
                      labelId="has-specific-registration-rules-label"
                      label="Existe regra específica para o cadastro de matrículas por categoria"
                    >
                      {hasSpecificRegistrationRulesOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.hasSpecificRegistrationRules?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {hasSpecificRegistrationRules === "1" && (
              <Grid item xs={12} md={6}>
                <Controller
                  name="specificRegistrationRulesDescription"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      disabled={admissionIsValidated}
                      label="Descreva a regra de negócio"
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={!!errors.specificRegistrationRulesDescription}
                      helperText={errors.specificRegistrationRulesDescription?.message}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* useSamePayrollRegistrationForTimeTracking — largura total, sem dependentes */}
        <Grid item xs={12}>
          <Controller
            name="useSamePayrollRegistrationForTimeTracking"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                size="small"
                required
                error={!!errors.useSamePayrollRegistrationForTimeTracking}
              >
                <InputLabel id="use-same-payroll-registration-label">
                  Utiliza a mesma matrícula da folha de pagamento no sistema do ponto
                </InputLabel>
                <Select
                  {...field}
                  required
                  disabled={admissionIsValidated}
                  labelId="use-same-payroll-registration-label"
                  label="Utiliza a mesma matrícula da folha de pagamento no sistema do ponto"
                >
                  {useSamePayrollRegistrationOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.useSamePayrollRegistrationForTimeTracking?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {/* workTimeControl + timeTrackingClosePeriod — dependente na mesma linha (md=4 cada quando "Outro") */}
        <Grid item xs={12} md={timeTrackingClosePeriod === "4" ? 4 : 6}>
          <Controller
            name="workTimeControl"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.workTimeControl}>
                <InputLabel id="work-time-control-label">
                  Realiza controle de jornada
                </InputLabel>
                <Select
                  {...field}
                  required
                  disabled={admissionIsValidated}
                  labelId="work-time-control-label"
                  label="Realiza controle de jornada"
                >
                  {workTimeControlOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.workTimeControl?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={timeTrackingClosePeriod === "4" ? 4 : 6}>
          <Controller
            name="timeTrackingClosePeriod"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.timeTrackingClosePeriod}>
                <InputLabel id="time-tracking-close-period-label">
                  Período de fechamento do ponto
                </InputLabel>
                <Select
                  {...field}
                  required
                  disabled={admissionIsValidated}
                  labelId="time-tracking-close-period-label"
                  label="Período de fechamento do ponto"
                >
                  {timeTrackingClosePeriodOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.timeTrackingClosePeriod?.message}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {timeTrackingClosePeriod === "4" && (
          <Grid item xs={12} md={4}>
            <Controller
              name="timeTrackingClosePeriodOther"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  disabled={admissionIsValidated}
                  label="Outro (ex: 01/31)"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{ inputComponent: ClosePeriodMask }}
                  error={!!errors.timeTrackingClosePeriodOther}
                  helperText={errors.timeTrackingClosePeriodOther?.message}
                />
              )}
            />
          </Grid>
        )}

        {/* hasBankOfHours — dependente na mesma linha */}
        <Grid item xs={12} md={6}>
          <Controller
            name="hasBankOfHours"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" required error={!!errors.hasBankOfHours}>
                <InputLabel id="has-bank-of-hours-label">
                  Paga banco de horas
                </InputLabel>
                <Select
                  {...field}
                  required
                  disabled={admissionIsValidated}
                  labelId="has-bank-of-hours-label"
                  label="Paga banco de horas"
                >
                  {hasBankOfHoursOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.hasBankOfHours?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        {hasBankOfHours === "1" && (
          <>
            <Grid item xs={12} md={6}>
              <Controller
                name="bankOfHoursFrequency"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" required error={!!errors.bankOfHoursFrequency}>
                    <InputLabel id="bank-of-hours-frequency-label">
                      Quantas vezes ao ano?
                    </InputLabel>
                    <Select
                      {...field}
                      required
                      disabled={admissionIsValidated}
                      labelId="bank-of-hours-frequency-label"
                      label="Quantas vezes ao ano?"
                    >
                      {bankOfHoursFrequencyOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.bankOfHoursFrequency?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="bankOfHoursCompetencies"
                control={control}
                render={({ field }) => {
                  const maxSelections = parseInt(bankOfHoursFrequency, 10) || 4;
                  const selected = Array.isArray(field.value) ? field.value : [];
                  return (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      disabled={admissionIsValidated || !bankOfHoursFrequency}
                      options={monthsOptions}
                      getOptionLabel={opt => opt.label}
                      value={monthsOptions.filter(m => selected.includes(m.value))}
                      isOptionEqualToValue={(opt, val) => opt.value === val.value}
                      getOptionDisabled={opt =>
                        !selected.includes(opt.value) && selected.length >= maxSelections
                      }
                      onChange={(_, newValue) => {
                        field.onChange(newValue.map(m => m.value));
                      }}
                      renderOption={(props, option, { selected: isSelected }) => (
                        <li {...props}>
                          <Checkbox checked={isSelected} size="small" sx={{ mr: 1 }} />
                          <ListItemText primary={option.label} />
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Em quais competências?"
                          size="small"
                          error={!!errors.bankOfHoursCompetencies}
                          helperText={errors.bankOfHoursCompetencies?.message}
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
}
